import { ReactNode, useEffect, useMemo } from 'react';
import { LoadingStatuses } from '@packages/shared/core/common/entities';
import { useLogger } from '@packages/shared/core/common/hooks';
import { extractFirstNElements } from '@packages/shared/core/common/utils/array/extractFirstNElements';
import { useExperts } from '@packages/shared/core/experts/hooks';
import { useQuizStep } from '@packages/shared/core/quiz/hooks';
import { ExpertsCatalogueStepData, QuizScreenProps } from '@packages/shared/core/quiz/entities';

type ExpertsCatalogueQuizStepProps = {
  id: string;
  renderQuizScreen: (stepData: QuizScreenProps) => ReactNode;
};

const initialExpertsAmount = 5;

const ExpertsCatalogueQuizStep = ({ id, renderQuizScreen }: ExpertsCatalogueQuizStepProps) => {
  const logger = useLogger();
  const { back, next, stepData, flowName } = useQuizStep(id);
  const {
    experts,
    currentExpertUpdated,
    fetchAllExperts,
    loadingAllExpertsStatus,
    fetchInitialExperts,
    loadingInitialExpertsStatus,
  } = useExperts({
    initialExpertsAmount,
    flowName,
    filter: { 'only-trial': 1 },
  });

  useEffect(() => {
    if (loadingInitialExpertsStatus !== LoadingStatuses.IDLE) return;

    fetchInitialExperts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customExperts = useMemo(() => {
    const copyExperts = [...experts];

    const expertsWithCustomSpecialization = extractFirstNElements(copyExperts, initialExpertsAmount).map((expert) => {
      const specializations = [...expert.specializations];

      return {
        ...expert,
        specializations: [...specializations],
      };
    });

    const expertsWithBestMatch = extractFirstNElements(expertsWithCustomSpecialization).map((expert) => ({
      ...expert,
      isBestMatch: false,
    }));

    return [...expertsWithBestMatch, ...expertsWithCustomSpecialization, ...copyExperts];
  }, [experts]);

  if (!stepData) {
    // TODO add fallback
    logger.error(new TypeError(`Data for ${id} step not found`));
    return null;
  }

  const chooseExpert = (expertId: string) => {
    currentExpertUpdated(expertId);
    next(expertId);
  };

  const isInitialExpertsLoaded =
    loadingInitialExpertsStatus !== LoadingStatuses.PENDING && loadingInitialExpertsStatus !== LoadingStatuses.IDLE;

  return (
    <>
      {renderQuizScreen({
        goBack: back,
        experts: customExperts,
        chooseExpert,
        loadingStatus: loadingAllExpertsStatus,
        isInitialExpertsLoaded,
        fetchMoreExperts: fetchAllExperts,

        ...(stepData as ExpertsCatalogueStepData),
      })}
    </>
  );
};

export default ExpertsCatalogueQuizStep;
